import { Link } from "react-router-dom";
import { ShieldCheck, Scale, AlertTriangle, Lock, FileText, ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="container-app py-12 max-w-3xl" data-testid="terms-page">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-6">
        <ArrowLeft size={14} /> Volver al inicio
      </Link>
      <div className="label-eyebrow">Términos y Política de Uso · Versión 1.0</div>
      <h1 className="serif text-4xl mt-3 leading-tight">Términos de Servicio de Colega Link</h1>
      <p className="text-sm text-muted-foreground mt-2">Última actualización: febrero 2026</p>

      <div className="mt-10 space-y-10 text-sm leading-relaxed">

        <section data-testid="section-naturaleza">
          <div className="flex items-center gap-2 mb-3">
            <Scale size={18} className="text-[hsl(var(--accent))]" />
            <h2 className="serif text-2xl">1. Naturaleza del servicio</h2>
          </div>
          <p>
            Colega Link es una <span className="text-foreground font-medium">plataforma de intermediación tecnológica</span> que conecta
            abogados y mediadores debidamente verificados ante la Corte Suprema de Chile. Nuestra función es exclusivamente
            poner en contacto a profesionales y administrar el flujo de Pago Protegido y la entrega de documentos
            relacionados con la gestión encargada.
          </p>
        </section>

        <section data-testid="section-exencion">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} className="text-amber-400" />
            <h2 className="serif text-2xl">2. Cláusula de exención de responsabilidad</h2>
          </div>
          <div className="panel p-5 bg-amber-400/5 border border-amber-400/30 space-y-3">
            <p className="font-medium text-amber-100">
              Colega Link NO es responsable por el éxito, resultado, calidad técnica o estrategia jurídica de las gestiones encargadas
              entre los profesionales registrados.
            </p>
            <p>
              Nuestra responsabilidad se limita estrictamente a:
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Verificar la inscripción de los profesionales en los registros de la Corte Suprema o Ministerio de Justicia.</li>
              <li>Custodiar operativamente el pago del mandante durante el flujo Pago Protegido, sin actuar como custodio financiero regulado.</li>
              <li>Mediar la comunicación entre el delegante y el delegado dentro de la plataforma.</li>
              <li>Liberar el pago al delegado una vez aprobado el comprobante por el delegante.</li>
            </ul>
            <p className="text-muted-foreground">
              Cualquier conflicto sobre la calidad, oportunidad o resultado de la gestión jurídica (audiencia, mediación,
              presentación de escritos, plazos procesales, estrategia, etc.) es responsabilidad exclusiva del profesional
              delegado y/o del cliente final. Colega Link no asume mandato judicial ni representación procesal de las partes.
            </p>
          </div>
        </section>

        <section data-testid="section-escrow">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={18} className="text-[hsl(var(--primary))]" />
            <h2 className="serif text-2xl">3. Pago Protegido Colega Link</h2>
          </div>
          <p>
            El <span className="text-foreground font-medium">Pago Protegido Colega Link</span> es un flujo operativo interno de la plataforma
            para la coordinación entre mandante y delegado. Describe únicamente el proceso de contratación, cumplimiento y liberación
            documentado en la plataforma, y <span className="text-foreground">no debe interpretarse como un servicio financiero regulado,
            depósito en garantía bancario ni custodia independiente</span> hasta que exista un proveedor de pagos habilitado con integración validada.
          </p>
          <p className="mt-2 text-muted-foreground">
            El delegado define libremente su honorario y lo recibe íntegramente al completarse la gestión.
            Colega Link cobra un cargo adicional al mandante por el uso de la plataforma: <span className="text-foreground">12% para el plan Free,
            5% para el plan Pionero (tras las 10 delegaciones iniciales sin comisión), 7% para el plan Premium (mínimo 0.2 UF) y 0% vitalicio para el plan
            Pionero Fundador</span> mientras la membresía esté vigente. Este cargo nunca se descuenta del honorario del delegado.
          </p>
          <p className="mt-2 text-muted-foreground">
            La liberación de fondos, reembolsos y resolución de controversias se rigen por los estados y reglas visibles en la plataforma.
            Mientras no exista integración financiera activa, la liberación se registra como acción administrativa y no como transferencia automática.
          </p>
        </section>

        <section data-testid="section-anonimato">
          <div className="flex items-center gap-2 mb-3">
            <Lock size={18} className="text-[hsl(var(--accent))]" />
            <h2 className="serif text-2xl">4. Prevención de desintermediación</h2>
          </div>
          <p>
            Para proteger la cartera de clientes y la integridad de la red, está <span className="text-foreground font-medium">expresamente
            prohibido</span> compartir números de teléfono, correos electrónicos, enlaces externos o referencias a aplicaciones de terceros
            (WhatsApp, Instagram, Telegram, etc.) en cualquier sección pública de la plataforma, incluyendo el foro de consultas profesionales.
          </p>
          <p className="mt-2 text-muted-foreground">
            Toda la comunicación entre profesionales relacionada con una gestión debe permanecer dentro de Colega Link.
            La violación reiterada de esta cláusula puede dar lugar a la suspensión de la cuenta sin derecho a reembolso.
          </p>
        </section>

        <section data-testid="section-membresia">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={18} className="text-[hsl(var(--primary))]" />
            <h2 className="serif text-2xl">5. Membresías y facturación</h2>
          </div>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>La membresía <span className="text-foreground">Pionero Fundador</span> se ofrece en modalidad mensual (0.75 UF) o anual (7.0 UF, con abono de $15.000 CLP de la reserva Pionero). Limitada a 50 cupos fundadores.</li>
            <li>El plan <span className="text-foreground">Pionero Fundador</span> mantiene su tarifa en UF, cuyo valor en CLP se calcula al día según mindicador.cl al momento de renovar.</li>
            <li>El plan <span className="text-foreground">Premium</span> se ofrece en modalidad mensual (0.85 UF) o anual (8.5 UF · ahorra 20%) y aplica una comisión de 7% con mínimo 0.2 UF por gestión.</li>
            <li>El plan Free no tiene costo mensual y puede usarse indefinidamente; el cargo Pago Protegido aplicable es del 12%.</li>
            <li>Cancelación: puedes cancelar tu suscripción en cualquier momento desde tu panel; el plan permanece activo hasta el final del periodo pagado, sin renovación automática.</li>
          </ul>
        </section>

        <section data-testid="section-verificacion">
          <h2 className="serif text-2xl mb-3">6. Verificación de identidad</h2>
          <p>
            Todos los profesionales deben subir su certificado vigente de inscripción en el registro de la Corte Suprema (abogados)
            o del Ministerio de Justicia (mediadores). Colega Link verifica manualmente este documento dentro de 24 horas hábiles.
            Una cuenta no verificada no puede publicar ni recibir delegaciones pagadas.
          </p>
        </section>

        <section data-testid="section-secreto">
          <h2 className="serif text-2xl mb-3">7. Confidencialidad y secreto profesional</h2>
          <p>
            Los archivos subidos a la plataforma (carpetas del caso, delegapoderes, comprobantes, actas) son visibles exclusivamente
            para las partes involucradas en cada gestión: titular y delegado. Colega Link no accede ni divulga estos contenidos,
            salvo orden judicial. El secreto profesional permanece bajo responsabilidad del abogado o mediador a cargo del caso.
          </p>
        </section>

        <section data-testid="section-foro">
          <h2 className="serif text-2xl mb-3">8. Foro de consultas profesionales</h2>
          <p>
            El foro de preguntas y respuestas tiene fines exclusivamente <span className="text-foreground">académicos y de consulta entre pares</span>.
            Las opiniones publicadas son responsabilidad del profesional que las emite y no constituyen asesoría legal vinculante.
            Se permite publicar de forma anónima para proteger la privacidad de casos sensibles.
          </p>
        </section>

        <section data-testid="section-jurisdiccion">
          <h2 className="serif text-2xl mb-3">9. Ley aplicable y jurisdicción</h2>
          <p>
            Estos términos se rigen por la legislación de la República de Chile. Cualquier controversia derivada del uso de Colega Link
            será sometida a los tribunales ordinarios de la ciudad de Santiago, sin perjuicio del derecho del usuario consumidor
            establecido en la Ley 19.496.
          </p>
        </section>

        <section data-testid="section-contacto">
          <h2 className="serif text-2xl mb-3">10. Contacto</h2>
          <p>
            Para consultas administrativas o disputas: <span className="text-foreground">contacto@colegalink.cl</span>. No utilizamos canales externos
            para gestiones operativas.
          </p>
        </section>

        <div className="panel p-5 mt-12 text-xs text-muted-foreground">
          Al registrarte o utilizar Colega Link, declaras haber leído y aceptado estos Términos de Servicio en su totalidad.
          Esta cláusula de exención de responsabilidad forma parte integral del contrato de uso.
        </div>
      </div>
    </div>
  );
}
